import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import {
  listAdmins,
  grantAdminByEmail,
  revokeAdmin,
  type AdminUser,
} from '@/lib/admin-users.functions'

export const Route = createFileRoute('/admin/users')({
  component: AdminUsersPage,
})

function fmt(ts: string) {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  })
}

function AdminUsersPage() {
  const navigate = useNavigate()
  const [checking, setChecking] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [granting, setGranting] = useState(false)
  const [revokingId, setRevokingId] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        navigate({ to: '/admin/login' })
        return
      }
      if (!mounted) return
      setCurrentUserId(session.user.id)
      const { data: roleRow } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .maybeSingle()
      if (!mounted) return
      setIsAdmin(!!roleRow)
      setChecking(false)
    }
    check()
  }, [navigate])

  const refresh = async () => {
    setLoading(true)
    try {
      const { admins } = await listAdmins()
      setAdmins(admins)
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to load admins')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAdmin) refresh()
  }, [isAdmin])

  const handleGrant = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    setGranting(true)
    try {
      const res = await grantAdminByEmail({ data: { email: trimmed } })
      if (res.alreadyAdmin) {
        toast.info(`${res.email} already has admin access`)
      } else {
        toast.success(`Granted admin to ${res.email}`)
      }
      setEmail('')
      await refresh()
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to grant admin')
    } finally {
      setGranting(false)
    }
  }

  const handleRevoke = async (u: AdminUser) => {
    if (!window.confirm(`Revoke admin access from ${u.email ?? u.user_id}?`)) return
    setRevokingId(u.user_id)
    try {
      await revokeAdmin({ data: { user_id: u.user_id } })
      toast.success('Admin access revoked')
      await refresh()
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to revoke admin')
    } finally {
      setRevokingId(null)
    }
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-bold">Not authorized</h1>
          <p className="text-sm text-muted-foreground">
            You don't have admin access.
          </p>
          <Link to="/admin"><Button variant="outline">Back</Button></Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Admin Users</h1>
            <p className="text-xs text-muted-foreground">
              Grant or revoke admin access
            </p>
          </div>
          <Link to="/admin">
            <Button variant="outline" size="sm">Back to dashboard</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Grant form */}
        <section className="border rounded-xl p-5 bg-card space-y-4">
          <div>
            <h2 className="font-semibold">Grant admin access</h2>
            <p className="text-xs text-muted-foreground">
              The user must already have an account. They'll need to sign up first if they don't.
            </p>
          </div>
          <form onSubmit={handleGrant} className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={granting}
              />
            </div>
            <Button type="submit" disabled={granting || !email.trim()}>
              {granting ? 'Granting…' : 'Grant admin'}
            </Button>
          </form>
        </section>

        {/* Admins list */}
        <section className="border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b bg-muted/30">
            <h2 className="font-semibold text-sm">Current admins ({admins.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Granted</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
                ) : admins.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">No admins found.</td></tr>
                ) : (
                  admins.map((u) => {
                    const isSelf = u.user_id === currentUserId
                    const isLastAdmin = admins.length <= 1
                    return (
                      <tr key={u.user_id} className="border-t">
                        <td className="px-4 py-3">
                          <div className="font-medium">{u.email ?? '(unknown email)'}</div>
                          {isSelf && (
                            <div className="text-xs text-muted-foreground">You</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{fmt(u.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRevoke(u)}
                            disabled={isSelf || isLastAdmin || revokingId === u.user_id}
                            title={
                              isSelf
                                ? "You can't revoke your own access"
                                : isLastAdmin
                                  ? "Can't revoke the last admin"
                                  : 'Revoke admin'
                            }
                          >
                            {revokingId === u.user_id ? 'Revoking…' : 'Revoke'}
                          </Button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  )
}
